import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, ModeBadge, ScopeChips, timeAgo } from "@/pages/Dashboard";
import { CopyButton } from "@/pages/Applications";
import {
  ArrowLeft, ArrowsClockwise, Prohibit, CreditCard, Receipt, Plus, ArrowSquareOut, Eye, EyeSlash
} from "@phosphor-icons/react";

export default function SubMerchantDetail() {
  const { id } = useParams();
  const [sm, setSm] = useState(null);
  const [tab, setTab] = useState("payments");
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const load = () => api.get(`/submerchants/${id}`).then(r => setSm(r.data)).catch(() => setSm(false));
  const loadPayments = () => api.get(`/submerchants/${id}/payments`)
    .then(r => { setPayments(r.data.items || []); setErrorMsg(""); })
    .catch(e => setErrorMsg(formatApiError(e.response?.data?.detail)));
  const loadOrders = () => api.get(`/submerchants/${id}/orders`)
    .then(r => { setOrders(r.data.items || []); setErrorMsg(""); })
    .catch(e => setErrorMsg(formatApiError(e.response?.data?.detail)));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { if (sm) { loadPayments(); loadOrders(); } /* eslint-disable-next-line */ }, [sm?.id]);

  if (sm === false) return <div className="text-sm text-slate-500">Sub-merchant not found.</div>;
  if (!sm) return <div className="text-sm text-slate-500">Loading…</div>;

  const refresh = async () => {
    try {
      await api.post(`/submerchants/${id}/refresh`);
      toast.success("Tokens refreshed");
      load(); loadPayments(); loadOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revoke = async () => {
    if (!confirm("Revoke authorization? The sub-merchant must re-authorize to grant access again.")) return;
    try {
      await api.post(`/submerchants/${id}/revoke`);
      toast.success("Authorization revoked");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const expiresInSec = Math.max(0, Math.floor((new Date(sm.expires_at).getTime() - Date.now()) / 1000));
  const daysLeft = Math.floor(expiresInSec / 86400);

  return (
    <div className="space-y-8">
      <div>
        <Link to="/submerchants" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#FF6B00]" data-testid="back-to-submerchants">
          <ArrowLeft size={12}/> All sub-merchants
        </Link>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-bold">{sm.business_name}</h1>
              <StatusBadge status={sm.status} />
              <ModeBadge mode={sm.mode} />
            </div>
            <div className="mt-1 flex items-center gap-3 text-sm text-slate-500">
              <span className="font-mono">{sm.razorpay_account_id}</span>
              <span>•</span>
              <span>{sm.email}</span>
              <span>•</span>
              <span>connected {timeAgo(sm.created_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={refresh} disabled={sm.status === "revoked"} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50" data-testid="refresh-tokens-button">
              <ArrowsClockwise size={12} /> Refresh tokens
            </button>
            <button onClick={revoke} disabled={sm.status === "revoked"} className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50" data-testid="revoke-button">
              <Prohibit size={12} /> Revoke
            </button>
          </div>
        </div>
      </div>

      {/* Token panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Access token" badge={`expires in ${daysLeft}d`}>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md bg-slate-50 px-3 py-2 font-mono text-xs">
              {reveal ? sm.access_token : sm.access_token_preview}
            </code>
            <button onClick={() => setReveal(r => !r)} className="text-slate-500 hover:text-[#FF6B00]" data-testid="toggle-access-reveal">
              {reveal ? <EyeSlash size={14}/> : <Eye size={14}/>}
            </button>
            <CopyButton value={sm.access_token} testId="copy-access-token"/>
          </div>
        </Card>
        <Card title="Refresh token">
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md bg-slate-50 px-3 py-2 font-mono text-xs">{sm.refresh_token_preview}</code>
            <CopyButton value={sm.refresh_token} testId="copy-refresh-token"/>
          </div>
        </Card>
        <Card title="Public token (Checkout)">
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md bg-slate-50 px-3 py-2 font-mono text-xs">{sm.public_token}</code>
            <CopyButton value={sm.public_token} testId="copy-public-token"/>
          </div>
        </Card>
      </div>

      <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Granted scopes</div>
          <ScopeChips scopes={sm.scopes} />
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {sm.scopes.map(s => (
            <span key={s} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[11px]">{s}</span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div>
        <div className="flex items-center gap-1 border-b border-[#E2E8F0]" data-testid="sm-tabs">
          {[
            { k: "payments", label: "Payments", icon: CreditCard },
            { k: "orders", label: "Orders", icon: Receipt },
          ].map(t => {
            const I = t.icon;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                className={`relative -mb-px inline-flex items-center gap-1.5 px-4 py-2.5 text-sm ${tab === t.k ? "text-[#FF6B00] border-b-2 border-[#FF6B00] font-medium" : "text-slate-500 hover:text-slate-800"}`}>
                <I size={14} /> {t.label}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setShowCreateOrder(true)} disabled={sm.status !== "active" || !sm.scopes.includes("read_write")} className="btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium disabled:opacity-50" data-testid="create-order-button" title={sm.scopes.includes("read_write") ? "" : "read_write scope required"}>
              <Plus size={12} weight="bold"/> Create Order
            </button>
            <button onClick={() => setShowCheckout(true)} disabled={sm.status !== "active"} className="inline-flex items-center gap-1.5 rounded-md border border-[#0F172A] bg-[#0F172A] px-3 py-2 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50" data-testid="open-checkout-demo">
              Checkout demo <ArrowSquareOut size={12}/>
            </button>
          </div>
        </div>

        {errorMsg && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="sm-error-banner">{errorMsg}</div>
        )}

        <div className="mt-4">
          {tab === "payments" && <PaymentsTable items={payments} />}
          {tab === "orders" && <OrdersTable items={orders} />}
        </div>
      </div>

      {showCreateOrder && <CreateOrderModal smId={sm.id} onClose={() => setShowCreateOrder(false)} onCreated={() => { setShowCreateOrder(false); loadOrders(); }} />}
      {showCheckout && <CheckoutDemoModal sm={sm} onClose={() => setShowCheckout(false)} />}
    </div>
  );
}

function Card({ title, badge, children }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</div>
        {badge && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">{badge}</span>}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function fmtAmount(paise, currency = "INR") {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(paise / 100);
}

function PaymentsTable({ items }) {
  if (items.length === 0) return <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="empty-payments">No payments yet.</div>;
  return (
    <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
      <table className="w-full text-sm" data-testid="payments-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr><Th>Payment</Th><Th>Amount</Th><Th>Method</Th><Th>Status</Th><Th>Order</Th><Th>Created</Th></tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {items.map(p => (
            <tr key={p.id} className="hover:bg-orange-50/40">
              <td className="px-4 py-3 font-mono text-xs">{p.id}</td>
              <td className="px-4 py-3 font-medium">{fmtAmount(p.amount, p.currency)}</td>
              <td className="px-4 py-3 capitalize">{p.method}{p.bank ? ` (${p.bank})` : ""}</td>
              <td className="px-4 py-3"><PayStatus status={p.status}/></td>
              <td className="px-4 py-3 font-mono text-xs">{p.order_id}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{new Date((p.created_at) * 1000).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrdersTable({ items }) {
  if (items.length === 0) return <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="empty-orders">No orders yet.</div>;
  return (
    <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
      <table className="w-full text-sm" data-testid="orders-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr><Th>Order</Th><Th>Amount</Th><Th>Status</Th><Th>Receipt</Th><Th>Created</Th></tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {items.map(o => (
            <tr key={o.id} className="hover:bg-orange-50/40">
              <td className="px-4 py-3 font-mono text-xs">{o.id}</td>
              <td className="px-4 py-3 font-medium">{fmtAmount(o.amount, o.currency)}</td>
              <td className="px-4 py-3"><PayStatus status={o.status}/></td>
              <td className="px-4 py-3 font-mono text-xs">{o.receipt}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{new Date((o.created_at) * 1000).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayStatus({ status }) {
  const map = {
    captured: "bg-[#D1FAE5] text-[#065F46]",
    authorized: "bg-blue-100 text-blue-700",
    failed: "bg-red-100 text-red-700",
    created: "bg-slate-100 text-slate-700",
    paid: "bg-[#D1FAE5] text-[#065F46]",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${map[status] || "bg-slate-100 text-slate-700"}`}>{status}</span>;
}

function Th({ children }) { return <th className="px-4 py-3 text-left font-semibold">{children}</th>; }

function CreateOrderModal({ smId, onClose, onCreated }) {
  const [amount, setAmount] = useState(50000);
  const [currency, setCurrency] = useState("INR");
  const [receipt, setReceipt] = useState("receipt#1");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const { data } = await api.post(`/submerchants/${smId}/orders`, { amount: Number(amount), currency, receipt });
      toast.success(`Order ${data.id} created`);
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl bg-white shadow-xl" data-testid="create-order-modal">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h3 className="font-display text-lg font-semibold">Create Order</h3>
          <p className="text-xs text-slate-500 mt-0.5">POST /v1/orders on behalf of the sub-merchant.</p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Amount (paise)">
            <input type="number" min="100" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="order-amount"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
            <p className="mt-1 text-[11px] text-slate-500">{fmtAmount(Number(amount || 0), currency)}</p>
          </Field>
          <Field label="Currency">
            <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} data-testid="order-currency"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm font-mono outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
          </Field>
          <Field label="Receipt">
            <input value={receipt} onChange={(e) => setReceipt(e.target.value)} data-testid="order-receipt"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#E2E8F0] px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-100" data-testid="order-cancel">Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="order-submit">
            {submitting ? "Creating…" : "Create order"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function CheckoutDemoModal({ sm, onClose }) {
  const snippet = `// Drop-in Razorpay Checkout using public_token (no key_id needed)
var options = {
  "key": "${sm.public_token}",     // public_token replaces key_id
  "amount": "29900",                  // in paise
  "currency": "INR",
  "name": "${sm.business_name}",
  "description": "Test Transaction",
  "order_id": "<ORDER_ID_FROM_BACKEND>",
  "callback_url": "https://your-app.com/payment/callback",
  "prefill": {
    "name": "Gaurav Kumar",
    "email": "gaurav.kumar@example.com",
    "contact": "9000090000"
  },
  "theme": { "color": "#FF6B00" }
};
var rzp = new Razorpay(options);
rzp.open();`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-xl bg-white shadow-xl" data-testid="checkout-modal">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h3 className="font-display text-lg font-semibold">Razorpay Checkout — public_token integration</h3>
          <p className="text-xs text-slate-500 mt-0.5">Use the sub-merchant's <code className="font-mono">public_token</code> as the <code className="font-mono">key</code> in your Checkout options. No <code className="font-mono">key_id</code> required.</p>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div className="code-block max-h-96 overflow-auto">{snippet}</div>
          <div className="flex justify-end gap-2">
            <CopyButton value={snippet} testId="copy-checkout-snippet" label="Snippet copied" />
          </div>
        </div>
      </div>
    </div>
  );
}
